import { useCallback } from 'react';
import { generateClientSideId, generateTempTaskId } from '../utils/clientIdGenerator';
import { trackEvent } from '../utils/analytics';

/**
 * Hook for all task CRUD mutations (local state + pending change tracking).
 * Does not perform API calls except updateTaskOrders (batch order sync).
 */
export function useTaskMutations({
  tasks,
  setTasks,
  currentFileId,
  pendingChangesRef,
  setHasPendingChanges,
  triggerAutoSave,
  isLoggedIn,
}) {
  const updateTaskStatus = useCallback(
    (taskId, newStatus, origin) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      setTasks((prevTasks) => {
        const prevTask = prevTasks.find((t) => t.id === taskId);
        if (!prevTask) return prevTasks;
        const updatedTask = { ...prevTask, status: newStatus };

        const existingModified = pendingChangesRef.current.modified.get(taskId);
        if (existingModified) {
          existingModified.status = newStatus;
        } else {
          pendingChangesRef.current.modified.set(taskId, {
            id: taskId,
            parent_task_id: prevTask.parent_task_id,
            text: prevTask.text,
            due_date: prevTask.due_date,
            is_later_date: prevTask.is_later_date || false,
            status: newStatus,
            order: prevTask.order,
            file_id: prevTask.file_id,
          });
        }
        return prevTasks.map((task) => (task.id === taskId ? updatedTask : task));
      });

      const createdIndex = pendingChangesRef.current.created.findIndex((t) => t.id === taskId);
      if (createdIndex !== -1) {
        pendingChangesRef.current.created[createdIndex] = {
          ...pendingChangesRef.current.created[createdIndex],
          status: newStatus,
        };
      }
      setHasPendingChanges(true);
      triggerAutoSave();

      if (currentTask && isLoggedIn && newStatus === 'complete') {
        trackEvent('task_completed');
      }
    },
    [
      tasks,
      setTasks,
      pendingChangesRef,
      setHasPendingChanges,
      triggerAutoSave,
      isLoggedIn,
    ]
  );

  const updateTaskTextLocal = useCallback(
    (taskId, newText) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      if (!currentTask) return;
      const updatedTask = { ...currentTask, text: newText };
      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );
      const createdIndex = pendingChangesRef.current.created.findIndex((t) => t.id === taskId);
      if (createdIndex !== -1) {
        pendingChangesRef.current.created[createdIndex] = {
          ...pendingChangesRef.current.created[createdIndex],
          text: newText,
        };
      }
    },
    [tasks, setTasks, pendingChangesRef]
  );

  const updateTaskText = useCallback(
    (taskId, newText) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      if (!currentTask) return;
      const updatedTask = { ...currentTask, text: newText };
      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );
      const createdIndex = pendingChangesRef.current.created.findIndex((t) => t.id === taskId);
      if (createdIndex !== -1) {
        pendingChangesRef.current.created[createdIndex] = {
          ...pendingChangesRef.current.created[createdIndex],
          text: newText,
        };
      }
      const existingModified = pendingChangesRef.current.modified.get(taskId);
      if (existingModified) {
        existingModified.text = newText;
      } else {
        pendingChangesRef.current.modified.set(taskId, {
          id: taskId,
          parent_task_id: currentTask.parent_task_id,
          text: newText,
          due_date: currentTask.due_date,
          is_later_date: currentTask.is_later_date || false,
          status: currentTask.status,
          order: currentTask.order,
          file_id: currentTask.file_id,
        });
      }
      setHasPendingChanges(true);
      triggerAutoSave();
    },
    [tasks, setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave]
  );

  const updateTaskDueDate = useCallback(
    (taskId, newDueDate, isLaterDateOrOrigin = false, maybeOrigin) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      if (!currentTask) return;
      const isLaterDateBool = typeof isLaterDateOrOrigin === 'boolean'
        ? (newDueDate ? isLaterDateOrOrigin === true : false)
        : false;
      const oldDueDate =
        currentTask.due_date == null || currentTask.due_date === ''
          ? null
          : currentTask.due_date;
      const newDueNormalized = newDueDate || null;

      // Subtasks that had the same due date as the parent get updated to the new due date
      const subtaskIdsToCascade = tasks
        .filter(
          (t) =>
            t.parent_task_id === taskId &&
            (t.due_date == null || t.due_date === '' ? null : t.due_date) ===
              oldDueDate
        )
        .map((t) => t.id);

      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          if (task.id === taskId) {
            return {
              ...task,
              due_date: newDueNormalized,
              is_later_date: isLaterDateBool,
            };
          }
          if (subtaskIdsToCascade.includes(task.id)) {
            return {
              ...task,
              due_date: newDueNormalized,
              is_later_date: isLaterDateBool,
            };
          }
          return task;
        })
      );

      const createdIndex = pendingChangesRef.current.created.findIndex(
        (t) => t.id === taskId
      );
      if (createdIndex !== -1) {
        pendingChangesRef.current.created[createdIndex] = {
          ...pendingChangesRef.current.created[createdIndex],
          due_date: newDueNormalized,
          is_later_date: isLaterDateBool,
        };
      }
      subtaskIdsToCascade.forEach((subId) => {
        const createdSubIndex = pendingChangesRef.current.created.findIndex(
          (t) => t.id === subId
        );
        if (createdSubIndex !== -1) {
          pendingChangesRef.current.created[createdSubIndex] = {
            ...pendingChangesRef.current.created[createdSubIndex],
            due_date: newDueNormalized,
            is_later_date: isLaterDateBool,
          };
        }
      });

      const existingModified = pendingChangesRef.current.modified.get(taskId);
      if (existingModified) {
        existingModified.due_date = newDueNormalized;
        existingModified.is_later_date = isLaterDateBool;
      } else {
        pendingChangesRef.current.modified.set(taskId, {
          id: taskId,
          parent_task_id: currentTask.parent_task_id,
          text: currentTask.text,
          due_date: newDueNormalized,
          is_later_date: isLaterDateBool,
          status: currentTask.status,
          order: currentTask.order,
          file_id: currentTask.file_id,
        });
      }
      subtaskIdsToCascade.forEach((subId) => {
        const subTask = tasks.find((t) => t.id === subId);
        if (!subTask) return;
        const existingSubModified =
          pendingChangesRef.current.modified.get(subId);
        if (existingSubModified) {
          existingSubModified.due_date = newDueNormalized;
          existingSubModified.is_later_date = isLaterDateBool;
        } else {
          pendingChangesRef.current.modified.set(subId, {
            id: subId,
            parent_task_id: subTask.parent_task_id,
            text: subTask.text,
            due_date: newDueNormalized,
            is_later_date: isLaterDateBool,
            status: subTask.status,
            order: subTask.order,
            file_id: subTask.file_id,
          });
        }
      });

      setHasPendingChanges(true);
      triggerAutoSave();
    },
    [tasks, setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave]
  );

  const updateTaskOrder = useCallback(
    (taskId, newOrder) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      if (!currentTask) return;
      const updatedTask = { ...currentTask, order: newOrder };
      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );
      const existingModified = pendingChangesRef.current.modified.get(taskId);
      if (existingModified) {
        existingModified.order = newOrder;
      } else {
        pendingChangesRef.current.modified.set(taskId, {
          id: taskId,
          parent_task_id: currentTask.parent_task_id,
          text: currentTask.text,
          due_date: currentTask.due_date,
          is_later_date: currentTask.is_later_date || false,
          status: currentTask.status,
          order: newOrder,
          file_id: currentTask.file_id,
        });
      }
      setHasPendingChanges(true);
      triggerAutoSave();
    },
    [tasks, setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave]
  );

  const updateTaskOrders = useCallback(
    async (taskOrders, optimistic = false, options = {}) => {
      if (!Array.isArray(taskOrders) || taskOrders.length === 0) {
        return { success: true };
      }
      const eventParams = options.view ? { view: options.view } : {};
      // When a task becomes a subtask (parent_task_id set) and has no due date, inherit from parent
      const enrichedOrders = taskOrders.map((to) => {
        const hasNewParent =
          to.parent_task_id !== undefined && to.parent_task_id !== null;
        if (!hasNewParent) return { ...to };
        const currentTask = tasks.find((t) => t.id === to.id);
        if (!currentTask) return { ...to };
        const currentDue = currentTask.due_date;
        if (currentDue !== null && currentDue !== '') return { ...to };
        const parentTask = tasks.find((t) => t.id === to.parent_task_id);
        if (!parentTask?.due_date) return { ...to };
        return {
          ...to,
          due_date: parentTask.due_date,
          is_later_date: parentTask.is_later_date ?? false,
        };
      });
      enrichedOrders.forEach(
        ({ id, order, prioritize_order, text, parent_task_id, due_date, is_later_date }) => {
          const createdIndex = pendingChangesRef.current.created.findIndex((t) => t.id === id);
          if (createdIndex === -1) return;
          const createdTask = { ...pendingChangesRef.current.created[createdIndex] };
          if (order !== undefined) createdTask.order = order;
          if (prioritize_order !== undefined) createdTask.prioritize_order = prioritize_order;
          if (text !== undefined && text !== null) createdTask.text = text;
          if (parent_task_id !== undefined) createdTask.parent_task_id = parent_task_id === null ? null : parent_task_id;
          if (due_date !== undefined) createdTask.due_date = due_date;
          if (is_later_date !== undefined) createdTask.is_later_date = is_later_date;
          pendingChangesRef.current.created[createdIndex] = createdTask;
        }
      );
      if (optimistic) {
        setTasks((prevTasks) => {
          const taskMap = new Map(prevTasks.map((t) => [t.id, t]));
          enrichedOrders.forEach(
            ({ id, order, prioritize_order, text, parent_task_id, due_date, is_later_date }) => {
              if (taskMap.has(id)) {
                const updatedTask = { ...taskMap.get(id) };
                if (order !== undefined) updatedTask.order = order;
                if (prioritize_order !== undefined)
                  updatedTask.prioritize_order = prioritize_order;
                if (text !== undefined && text !== null) updatedTask.text = text;
                if (parent_task_id !== undefined)
                  updatedTask.parent_task_id =
                    parent_task_id === null ? null : parent_task_id;
                if (due_date !== undefined) updatedTask.due_date = due_date;
                if (is_later_date !== undefined)
                  updatedTask.is_later_date = is_later_date;
                taskMap.set(id, updatedTask);
              }
            }
          );
          return Array.from(taskMap.values());
        });
      }
      if (!isLoggedIn) {
        if (!optimistic) {
          setTasks((prevTasks) => {
            const taskMap = new Map(prevTasks.map((t) => [t.id, t]));
            enrichedOrders.forEach(
              ({ id, order, prioritize_order, text, parent_task_id, due_date, is_later_date }) => {
                if (taskMap.has(id)) {
                  const updatedTask = { ...taskMap.get(id) };
                  if (order !== undefined) updatedTask.order = order;
                  if (prioritize_order !== undefined)
                    updatedTask.prioritize_order = prioritize_order;
                  if (text !== undefined && text !== null) updatedTask.text = text;
                  if (parent_task_id !== undefined)
                    updatedTask.parent_task_id =
                      parent_task_id === null ? null : parent_task_id;
                  if (due_date !== undefined) updatedTask.due_date = due_date;
                  if (is_later_date !== undefined)
                    updatedTask.is_later_date = is_later_date;
                  taskMap.set(id, updatedTask);
                }
              }
            );
            return Array.from(taskMap.values());
          });
        }
        if (options.origin === 'drag') {
          trackEvent('logged_out_interaction_tasks_reordered', eventParams);
        }
        return { success: true };
      }
      try {
        const response = await fetch('/api/tasks.php', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batch_orders: enrichedOrders }),
        });
        if (response.ok) {
          if (!optimistic) {
            setTasks((prevTasks) => {
              const taskMap = new Map(prevTasks.map((t) => [t.id, t]));
              enrichedOrders.forEach(
                ({
                  id,
                  order,
                  prioritize_order,
                  text,
                  parent_task_id,
                  due_date,
                  is_later_date,
                }) => {
                  if (taskMap.has(id)) {
                    const updatedTask = { ...taskMap.get(id) };
                    if (order !== undefined) updatedTask.order = order;
                    if (prioritize_order !== undefined)
                      updatedTask.prioritize_order = prioritize_order;
                    if (text !== undefined && text !== null) updatedTask.text = text;
                    if (parent_task_id !== undefined)
                      updatedTask.parent_task_id =
                        parent_task_id === null ? null : parent_task_id;
                    if (due_date !== undefined) updatedTask.due_date = due_date;
                    if (is_later_date !== undefined)
                      updatedTask.is_later_date = is_later_date;
                    taskMap.set(id, updatedTask);
                  }
                }
              );
              return Array.from(taskMap.values());
            });
          }
          if (options.origin === 'drag') {
            trackEvent('tasks_reordered', eventParams);
          }
          return { success: true };
        }
        const error = await response.json();
        throw new Error(error.error || 'Failed to update task orders');
      } catch (error) {
        console.error('Error updating task orders:', error);
        throw error;
      }
    },
    [tasks, setTasks, pendingChangesRef, isLoggedIn]
  );

  const updateTaskParent = useCallback(
    (taskId, newParentTaskId) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      if (!currentTask) return;
      const updatedTask = { ...currentTask, parent_task_id: newParentTaskId };
      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );
      const existingModified = pendingChangesRef.current.modified.get(taskId);
      if (existingModified) {
        existingModified.parent_task_id = newParentTaskId;
      } else {
        pendingChangesRef.current.modified.set(taskId, {
          id: taskId,
          parent_task_id: newParentTaskId,
          text: currentTask.text,
          due_date: currentTask.due_date,
          is_later_date: currentTask.is_later_date || false,
          status: currentTask.status,
          order: currentTask.order,
          file_id: currentTask.file_id,
        });
      }
      setHasPendingChanges(true);
      triggerAutoSave();
    },
    [tasks, setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave]
  );

  const updateTaskParentWithText = useCallback(
    (taskId, newParentTaskId, text) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      if (!currentTask) return;
      const textToSave =
        text !== null && text !== undefined ? text : currentTask.text || '';
      const updatedTask = {
        ...currentTask,
        parent_task_id:
          newParentTaskId === null || newParentTaskId === undefined
            ? null
            : newParentTaskId,
        text: textToSave,
      };
      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );
      const existingModified = pendingChangesRef.current.modified.get(taskId);
      if (existingModified) {
        existingModified.parent_task_id =
          newParentTaskId === null || newParentTaskId === undefined
            ? null
            : newParentTaskId;
        existingModified.text = textToSave;
      } else {
        pendingChangesRef.current.modified.set(taskId, {
          id: taskId,
          parent_task_id:
            newParentTaskId === null || newParentTaskId === undefined
              ? null
              : newParentTaskId,
          text: textToSave,
          due_date: currentTask.due_date,
          is_later_date: currentTask.is_later_date || false,
          status: currentTask.status,
          order: currentTask.order,
          file_id: currentTask.file_id,
        });
      }
      setHasPendingChanges(true);
      triggerAutoSave();
    },
    [tasks, setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave]
  );

  const updateTaskParentWithTextAndOrder = useCallback(
    (taskId, newParentTaskId, text, order) => {
      const currentTask = tasks.find((t) => t.id === taskId);
      if (!currentTask) return;
      const textToSave =
        text !== null && text !== undefined ? text : currentTask.text || '';
      let finalDueDate = currentTask.due_date;
      if (
        newParentTaskId !== null &&
        newParentTaskId !== undefined &&
        (finalDueDate === null || finalDueDate === '')
      ) {
        const parentTask = tasks.find((t) => t.id === newParentTaskId);
        if (parentTask?.due_date) finalDueDate = parentTask.due_date;
      }
      const updatedTask = {
        ...currentTask,
        parent_task_id:
          newParentTaskId === null || newParentTaskId === undefined
            ? null
            : newParentTaskId,
        text: textToSave,
        order: order !== null && order !== undefined ? order : null,
        due_date: finalDueDate || null,
      };
      setTasks((prevTasks) =>
        prevTasks.map((task) => (task.id === taskId ? updatedTask : task))
      );
      const createdIndex = pendingChangesRef.current.created.findIndex(
        (t) => t.id === taskId
      );
      if (createdIndex !== -1) {
        pendingChangesRef.current.created[createdIndex] = {
          ...pendingChangesRef.current.created[createdIndex],
          parent_task_id:
            newParentTaskId === null || newParentTaskId === undefined
              ? null
              : newParentTaskId,
          text: textToSave,
          order: order !== null && order !== undefined ? order : null,
          due_date: finalDueDate || null,
        };
      }
      const existingModified = pendingChangesRef.current.modified.get(taskId);
      if (existingModified) {
        existingModified.parent_task_id =
          newParentTaskId === null || newParentTaskId === undefined
            ? null
            : newParentTaskId;
        existingModified.text = textToSave;
        existingModified.order =
          order !== null && order !== undefined ? order : null;
        existingModified.due_date = finalDueDate || null;
        existingModified.is_later_date = finalDueDate
          ? (currentTask.is_later_date || false)
          : false;
      } else {
        pendingChangesRef.current.modified.set(taskId, {
          id: taskId,
          parent_task_id:
            newParentTaskId === null || newParentTaskId === undefined
              ? null
              : newParentTaskId,
          text: textToSave,
          due_date: finalDueDate || null,
          is_later_date: finalDueDate
            ? (currentTask.is_later_date || false)
            : false,
          status: currentTask.status,
          order: order !== null && order !== undefined ? order : null,
          file_id: currentTask.file_id,
        });
      }
      setHasPendingChanges(true);
      triggerAutoSave();
    },
    [tasks, setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave]
  );

  const createTask = useCallback(
    (
      text,
      parentTaskId,
      order,
      status = 'incomplete',
      dueDate = null,
      fileId = null,
      isLaterDate = false,
      origin
    ) => {
      const taskFileId = fileId !== null ? fileId : currentFileId;
      let finalDueDate = dueDate;
      const explicitDue =
        finalDueDate != null && finalDueDate !== '';
      let finalIsLaterDate = explicitDue ? isLaterDate === true : false;
      if (
        parentTaskId != null &&
        (finalDueDate === null || finalDueDate === '')
      ) {
        const parentTask = tasks.find((t) => t.id === parentTaskId);
        if (parentTask?.due_date) {
          finalDueDate = parentTask.due_date;
          finalIsLaterDate = parentTask.is_later_date === true;
        }
      }
      const tempId = generateTempTaskId();
      const reactKey = generateClientSideId();
      const newTask = {
        id: tempId,
        _reactKey: reactKey,
        text,
        parent_task_id: parentTaskId || null,
        status,
        order: order !== null && order !== undefined ? order : null,
        due_date: finalDueDate || null,
        is_later_date: finalIsLaterDate,
        prioritize_order: null,
        file_id: taskFileId,
        created_at: new Date().toISOString(),
      };
      setTasks((prev) => [...prev, newTask]);
      pendingChangesRef.current.created.push(newTask);
      setHasPendingChanges(true);
      triggerAutoSave();

      // Default parent to expanded when it gets a new subtask
      if (parentTaskId != null) {
        const parentTask = tasks.find((t) => t.id === parentTaskId);
        if (parentTask && parentTask.status !== 'expanded') {
          updateTaskStatus(parentTaskId, 'expanded');
        }
      }

      if (isLoggedIn) {
        trackEvent('task_created');
      } else {
        const prioritizeSection =
          origin &&
          typeof origin === 'object' &&
          origin.prioritizeSection
            ? origin.prioritizeSection
            : null;
        if (prioritizeSection) {
          trackEvent(
            `logged_out_interaction_task_created_prioritize_${prioritizeSection}`
          );
        } else {
          trackEvent('logged_out_interaction_task_created');
        }
      }
      return newTask;
    },
    [
      tasks,
      currentFileId,
      setTasks,
      pendingChangesRef,
      setHasPendingChanges,
      triggerAutoSave,
      updateTaskStatus,
      isLoggedIn,
    ]
  );

  const deleteTask = useCallback(
    (taskId, origin) => {
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
      pendingChangesRef.current.created = pendingChangesRef.current.created.filter(
        (t) => t.id !== taskId
      );
      pendingChangesRef.current.modified.delete(taskId);
      if (!String(taskId).startsWith('temp_')) {
        pendingChangesRef.current.deleted.push(taskId);
      }
      setHasPendingChanges(true);
      triggerAutoSave();
      if (isLoggedIn) {
        trackEvent('task_deleted');
      } else {
        trackEvent('logged_out_interaction_task_deleted');
      }
      return true;
    },
    [setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave, isLoggedIn]
  );

  const clearAllTasks = useCallback(() => {
    setTasks((prevTasks) => {
      const idsToDelete = prevTasks
        .filter((t) => !String(t.id).startsWith('temp_'))
        .map((t) => t.id);
      pendingChangesRef.current.created = [];
      pendingChangesRef.current.modified.clear();
      pendingChangesRef.current.deleted.push(...idsToDelete);
      setHasPendingChanges(true);
      triggerAutoSave();
      return [];
    });
  }, [setTasks, pendingChangesRef, setHasPendingChanges, triggerAutoSave]);

  return {
    updateTaskStatus,
    updateTaskTextLocal,
    updateTaskText,
    updateTaskDueDate,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParent,
    updateTaskParentWithText,
    updateTaskParentWithTextAndOrder,
    createTask,
    deleteTask,
    clearAllTasks,
  };
}
